import { toFullState } from '@/utilities/address';
import { toNational } from '@/utilities/formatPhoneNumber';
import {
    PLACEHOLDER_OVERRIDE_TYPES,
    SETUP_STATE,
    SETUP_TYPES,
    SHIPPER_SCOPED_OVERRIDE_TYPES,
    UNKNOWN_SETUP_STATE,
} from './constants';

/** The carrier's alias for the shipper, else the shipper's business name. */
export const getShipperName = (partnership) =>
    partnership?.shipper_alias || partnership?.shipper?.business_name || 'Unnamed shipper';

/**
 * Weakest first. A line shows its strongest state, as the corporate Integrations card does: any
 * ERROR wins, then any PENDING, then ENABLED.
 */
const SETUP_STATE_RANK = [
    SETUP_STATE.DISABLED.key,
    SETUP_STATE.ENABLED.key,
    SETUP_STATE.PENDING.key,
    SETUP_STATE.ERROR.key,
];

const overrideState = ({ status }) => {
    if (status === 'COMPLETE') return SETUP_STATE.ENABLED.key;
    if (status === 'PENDING') return SETUP_STATE.PENDING.key;
    return SETUP_STATE.ERROR.key;
};

const countsAsCarrierIntegration = (override) =>
    Boolean(override?.shipper_id) &&
    !SHIPPER_SCOPED_OVERRIDE_TYPES.includes(override.type) &&
    !PLACEHOLDER_OVERRIDE_TYPES.includes(override.type);

/** No connection set up with the shipper. */
export const NO_SETUP = Object.fromEntries(SETUP_TYPES.map(({ key }) => [key, SETUP_STATE.DISABLED.key]));

/**
 * `{ [shipper_id]: { webhook, edi, integrations } }` as `SETUP_STATE` keys; shippers with nothing set up
 * are absent. EDI overrides have an ISA id. A line shows its strongest state (ERROR, then PENDING).
 */
export const getSetupStatusByShipper = ({ hooks = [], overrides = [] } = {}) => {
    const byShipper = {};
    const mark = (shipperId, key, state) => {
        byShipper[shipperId] ||= { ...NO_SETUP };
        const current = byShipper[shipperId][key];
        if (SETUP_STATE_RANK.indexOf(state) > SETUP_STATE_RANK.indexOf(current)) byShipper[shipperId][key] = state;
    };

    hooks.forEach((hook) => {
        if (hook?.shipper_id) mark(hook.shipper_id, 'webhook', SETUP_STATE.ENABLED.key);
    });

    overrides.forEach((override) => {
        if (!countsAsCarrierIntegration(override)) return;
        const isEdi = Boolean(override.rec_isa_id || override.send_isa_id);
        mark(override.shipper_id, isEdi ? 'edi' : 'integrations', overrideState(override));
    });

    return byShipper;
};

/** Up to two initials from a name ("Shipper Company" → "SC"), for an avatar with no logo. */
export const getInitials = (name) =>
    (name || '')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .reduce((initials, word) => initials + (word[0]?.toUpperCase() || ''), '');

/** The shipper's business address on one line, or null when it has none. */
export const formatShipperAddress = (shipper) => {
    if (!shipper?.business_address) return null;
    return [
        shipper.business_address,
        shipper.business_address_unit,
        shipper.business_city,
        toFullState(shipper.business_state),
        shipper.business_zip,
    ]
        .filter(Boolean)
        .join(', ');
};

/** The shipper's business phone in national format, or null when it has none. */
export const formatShipperPhone = (shipper) =>
    shipper?.business_phone ? toNational(shipper.business_phone, shipper.business_phone) : null;

/**
 * The connection type(s) set up with the shipper, from its setup status: "Webhook", "EDI, Integrations",
 * "None", or "—" when the status isn't known.
 * @param {{ webhook: string, edi: string, integrations: string } | null | undefined} setup `SETUP_STATE`
 *   keys, or null / undefined while loading or after a failed read
 */
export const getConnectionTypeLabel = (setup) => {
    if (!setup) return UNKNOWN_SETUP_STATE.label;
    return (
        SETUP_TYPES.flatMap(({ key, label }) => (setup[key] === SETUP_STATE.ENABLED.key ? label : [])).join(', ') ||
        'None'
    );
};

/**
 * The logo and background color shown for a shipper: the carrier's partnership branding when set,
 * otherwise the shipper's own.
 * @returns {{ logo: string | null, color: string | null }}
 */
export const getShipperBranding = (partnership) => ({
    logo: partnership?.profile_img || partnership?.shipper?.profile_img || null,
    color: partnership?.profile_img_color || partnership?.shipper?.profile_img_color || null,
});

/**
 * Black or white, whichever reads better on the given hex background. Falls back to black for
 * anything that isn't a 3- or 6-digit hex color.
 * @param {string} hex e.g. "#0C0B1D"
 * @returns {'#000000' | '#FFFFFF'}
 */
export const getReadableTextColor = (hex) => {
    const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex || '');
    if (!match) return '#000000';
    const digits = match[1].length === 3 ? [...match[1]].map((d) => d + d).join('') : match[1];
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16) / 255);
    // Perceived brightness (ITU-R BT.601 weights).
    return 0.299 * r + 0.587 * g + 0.114 * b > 0.55 ? '#000000' : '#FFFFFF';
};
